import { Ref, ref, watch } from 'vue'

import { IncidentId } from '@collector/sportsapi-client-legacy'
import { AssignIncidentOnPitchTable } from '@desktop/utils/database'
import { useEvent, useIncidents } from '@desktop/views/Relation/composables'
import { AssignIncidentConfigIncidents } from '@desktop/views/Relation/Sports/configuration/assignIncidentOnPitchConfig'
import { injectAssignIncidentOnPitchConfig } from '@desktop/views/Relation/Sports/provide-inject'
import { IncidentUpdate } from '@desktop/views/Relation/utils'

type AssignedIncidentOnPitch = {
  incidentId: IncidentId
  participantTeamId: number
  xPos?: number
  yPos?: number
}

export type UseAssignIncidentOnPitch = {
  currentIncident: Ref<AssignedIncidentOnPitch | null>
  incidentsToDisplay: Ref<AssignIncidentConfigIncidents<IncidentId>[]>
  deleteIncident: () => Promise<void>
  setIncident: (opts: AssignedIncidentOnPitch) => Promise<void>
}

// state
const currentIncident = ref<AssignedIncidentOnPitch | null>(null)
const incidentsToDisplay = ref<AssignIncidentConfigIncidents<IncidentId>[]>([])

export function useAssignIncidentOnPitch(): UseAssignIncidentOnPitch {
  // inject
  const assignIncidentOnPitchConfig = injectAssignIncidentOnPitchConfig()
  const assignIncidentOnPitchIncidents =
    assignIncidentOnPitchConfig?.assignIncidentOnPitchIncidents
  // composables
  const { event } = useEvent()

  // methods
  async function setIncident({
    incidentId,
    participantTeamId,
    xPos,
    yPos,
  }: AssignedIncidentOnPitch): Promise<void> {
    currentIncident.value = { incidentId, participantTeamId, xPos, yPos }
    incidentsToDisplay.value =
      assignIncidentOnPitchIncidents?.get(incidentId) || []
    await AssignIncidentOnPitchTable.setIncident(
      event.value.id,
      incidentId,
      participantTeamId,
      xPos,
      yPos,
    )
  }

  async function deleteIncident(): Promise<void> {
    currentIncident.value = null
    incidentsToDisplay.value = []
    await AssignIncidentOnPitchTable.remove(event.value.id)
  }

  return {
    currentIncident,
    incidentsToDisplay,
    deleteIncident,
    setIncident,
  }
}

export async function initAssignIncidentOnPitch(): Promise<void> {
  const assignIncidentOnPitchConfig = injectAssignIncidentOnPitchConfig()
  const assignIncidentOnPitchIncidents =
    assignIncidentOnPitchConfig?.assignIncidentOnPitchIncidents

  const { event } = useEvent()
  const { incidentsUpdate } = useIncidents()
  const { deleteIncident, setIncident } = useAssignIncidentOnPitch()

  function isAssignIncidentOnPitchIncident<T extends IncidentId>(
    incidentId: T,
  ): boolean {
    return assignIncidentOnPitchIncidents?.has(incidentId) || false
  }

  function isIncidentMessageRelatedToCurrentIncident(
    incidentMessage: IncidentUpdate,
  ): boolean {
    const currentIncidentId = currentIncident.value?.incidentId
    if (!currentIncidentId) return false

    const currentIncidentRelatedIncidents =
      assignIncidentOnPitchIncidents?.get(currentIncidentId)
    if (!currentIncidentRelatedIncidents) return false

    return (
      currentIncidentRelatedIncidents.find(
        (relatedIncident) =>
          relatedIncident.incidentId === incidentMessage.data.incident_id,
      ) !== undefined
    )
  }

  watch(incidentsUpdate, async (incidentMessages) => {
    for (const incidentMessage of incidentMessages) {
      switch (incidentMessage.data.action) {
        case 'insert':
          if (isIncidentMessageRelatedToCurrentIncident(incidentMessage))
            await deleteIncident()
          if (
            isAssignIncidentOnPitchIncident(incidentMessage.data.incident_id)
          ) {
            const data = incidentMessage.data
            await setIncident({
              incidentId: data.incident_id,
              participantTeamId: data.participant_team_id,
              xPos: data.x_pos ?? undefined,
              yPos: data.y_pos ?? undefined,
            })
          }
          break
      }
    }
  })

  const assignIncidentOnPitchTableRecord = await AssignIncidentOnPitchTable.get(
    event.value.id,
  )

  if (assignIncidentOnPitchTableRecord) {
    const { incidentId, participantTeamId, xPos, yPos } =
      assignIncidentOnPitchTableRecord
    const isValidIncident = isAssignIncidentOnPitchIncident(incidentId)
    const isValidParticipant =
      participantTeamId !== null && participantTeamId !== undefined

    if (!isValidIncident || !isValidParticipant) {
      await deleteIncident()
      return
    }

    await setIncident({ incidentId, participantTeamId, xPos, yPos })
  }
}
